import React from 'react';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';

import MetaTags from 'components/meta-tags';
import { Button } from 'components/core';
import { SmallLoader } from 'shared/components';
import { DutchieLoyaltyWidget } from 'src/components/widgets/dutchie-loyalty-widget';
import useUI from 'hooks/use-ui';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

import type { LoyaltyRewardsProps } from '../types';

export function LoyaltyRewards({
  enterprise,
  rewards = null,
  loyaltyConfigData = null,
  rewardsDataLoading = false,
  loyaltyConfigLoading = false,
  isEmbedMode = false,
}: LoyaltyRewardsProps): JSX.Element {
  const router = useRouter();
  const UI = useUI();

  const RemoteLoyaltyWidget = useRemoteComponent<React.ComponentProps<typeof DutchieLoyaltyWidget>>(
    RemoteComponentSlot.LoyaltyWidget,
    { fallback: DutchieLoyaltyWidget }
  );

  const isPoints = loyaltyConfigData?.tierEarningBasis === 'points';
  const hasTiers = (loyaltyConfigData?.tiers?.length ?? 0) > 0;
  const redemptionType = loyaltyConfigData?.redemptionType?.toLowerCase() ?? '';
  const rewardLabel = isPoints ? 'points' : 'dollars';

  const handleShopNow = (): void => {
    const url = enterprise?.landingPageUrl ?? '/';

    if (isEmbedMode) {
      UI.embeddedEventDispatcher('loyalty:success', {
        enterpriseId: enterprise?.enterpriseId ?? null,
        landingPageUrl: url,
      });
      return;
    }

    void router?.push(url);
  };

  return (
    <>
      <MetaTags title='Welcome to Rewards Program' description='Start earning rewards on every purchase' />
      <LoyaltyContainer>
        <LoyaltyContent>
          <WelcomeTitle>
            Welcome to {String(enterprise?.displayName ?? enterprise?.name ?? 'Company/Program')}
          </WelcomeTitle>
          {loyaltyConfigLoading || rewardsDataLoading ? (
            <LoadingCard>
              <SmallLoader />
            </LoadingCard>
          ) : (
            <RemoteLoyaltyWidget
              hasTiers={hasTiers}
              redemptionType={redemptionType}
              isWidgetLink={false}
              locationMargin='0px auto 16px'
              displayCTA={false}
              activeEnterpriseId={enterprise?.enterpriseId}
              loyaltyEnterpriseConfigData={loyaltyConfigData ?? undefined}
              rewardsEnterpriseData={rewards ?? undefined}
            />
          )}

          {!loyaltyConfigLoading && !rewardsDataLoading && (
            <>
              <BenefitsText>
                Start earning {rewardLabel} on every purchase, get exclusive member discounts and access to special
                offers & early sales.
              </BenefitsText>

              <ButtonContainer>
                <ShopButton onClick={handleShopNow}>SHOP NOW</ShopButton>
              </ButtonContainer>
            </>
          )}
        </LoyaltyContent>
      </LoyaltyContainer>
    </>
  );
}

// Styled Components for Loyalty Rewards
const LoyaltyContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 16px;
`;

const LoyaltyContent = styled.div`
  width: 100%;
  max-width: 600px;
  text-align: left;
`;

const WelcomeTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 24px 0;
  line-height: 1.2;
  text-align: center;
`;

const LoadingCard = styled.div`
  background: var(--dct--customized-colors-buttonsLinks);
  border-radius: var(--dct--customized-radius-lg);
  padding: 64px 32px;
  margin-bottom: 32px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const BenefitsText = styled.p`
  font-size: 18px;
  color: #4b5563;
  line-height: 1.6;
  margin: 0 0 32px 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ShopButton = styled(Button)`
  font-size: 16px;
  padding: 16px 48px;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;
